import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { CartItem } from '../components/restaurant/CartItem';
import { CheckoutUpsellModal } from '../components/restaurant/CheckoutUpsellModal';
import { EmiliusCheckoutUpsellModal } from '../components/restaurant/EmiliusCheckoutUpsellModal';
import { useCart } from '../lib/cart';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { useKitchenPause } from '../lib/kitchen-pause-context';
import { useLanguage } from '../lib/i18n';
import { useTableOrder } from '../lib/table-context';
import { useRestaurantScheduleStatus } from '../lib/schedule';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/supabase';
import { ShoppingBag, ArrowLeft, Store, CheckCircle2, Loader2, Utensils, AlertCircle } from 'lucide-react';
export function CartPage() {
  const navigate = useNavigate();
  // La ruta /r/:slug/cart SÍ expone el slug aunque el carrito esté vacío.
  // Es la señal MÁS confiable para saber que estamos en el flujo de
  // Emilius (cuando el carrito se vacía perdemos cart.restaurantId/slug,
  // pero la URL sigue diciendo emilius-pizza).
  const {
    slug: urlSlug
  } = useParams<{
    slug: string;
  }>();
  const {
    cart,
    updateQuantity,
    removeItem,
    getTotal,
    clearCart,
    getDiscount
  } = useCart();
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  const {
    t
  } = useLanguage();
  const {
    tableOrder,
    isTableOrder,
    clearTableOrder
  } = useTableOrder();
  // Schedule-based open/closed enforcement
  const scheduleStatus = useRestaurantScheduleStatus(cart.restaurantSlug);
  const isClosedBySchedule = scheduleStatus.isClosedForOrdering;
  // QR self-service dine-in restaurants (NatuCafé id 19, Rancho id 28).
  // When the customer scanned a table QR, the checkout flow is simplified
  // and the cart button reflects "send to kitchen" rather than "pay".
  const isQrSelfServiceTable = isTableOrder && !!tableOrder && (String(tableOrder.restaurantId) === '19' || String(tableOrder.restaurantId) === '28');
  // QR self-service ahora cubre Rancho (28) y NatuCafé (19) — ambos
  // usan one-tap submit desde el botón "Enviar Pedido". Se gating
  // hace con isQrSelfServiceTable arriba; no necesitamos un flag
  // exclusivo a Rancho aquí.
  // ─── Crack Pollo (id 20) upsell modal ────────────────────────────────
  const isCrackPolloCart = String(cart.restaurantId) === '20';
  // ─── Emilius Pizza (id 27) upsell modal ──────────────────────────────
  // Incluímos el slug como respaldo: aunque el carrito quede vacío y se
  // pierda restaurantId, mientras conservemos el slug seguimos sabiendo
  // que estamos en el flujo white-label de Emilius (para no mandar al
  // cliente al marketplace de NOIZA desde el carrito vacío).
  const isEmiliusCart = String(cart.restaurantId) === '27' || cart.restaurantSlug === 'emilius-pizza' || urlSlug === 'emilius-pizza';
  // ─── Kyomu Sushi (id 21) upsell modal ────────────────────────────────
  const isKyomuCart = String(cart.restaurantId) === '21';
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [showEmiliusUpsellModal, setShowEmiliusUpsellModal] = useState(false);
  const [showKyomuUpsellModal, setShowKyomuUpsellModal] = useState(false);
  const [upsellAlreadyShown, setUpsellAlreadyShown] = useState(false);
  // ── Rancho QR direct-submit state ─────────────────────────────────────
  const [ranchoSubmitState, setRanchoSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [ranchoOrderNumber, setRanchoOrderNumber] = useState<string | null>(null);
  const [ranchoSubmitError, setRanchoSubmitError] = useState<string | null>(null);
  // Snapshot of the table name + items at submit time, so the success
  // screen survives clearCart() / clearTableOrder().
  const [ranchoSuccessSnapshot, setRanchoSuccessSnapshot] = useState<{
    tableName: string;
    items: typeof cart.items;
    total: number;
  } | null>(null);
  // Cart totals (declared early so submitRanchoQrOrder can reference them).
  const subtotal = getTotal();
  const discount = getDiscount();
  const submitRanchoQrOrder = async () => {
    if (!tableOrder || !cart.restaurantId || cart.items.length === 0) return;
    if (ranchoSubmitState === 'submitting') return;
    setRanchoSubmitState('submitting');
    setRanchoSubmitError(null);
    try {
      const orderIntentId = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `rancho-qr-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const itemsPayload = cart.items.map(item => ({
        menu_item_id: item.menu_item_id,
        item_name_snapshot: item.item_name_snapshot,
        unit_price_snapshot: item.unit_price_snapshot,
        quantity: item.quantity,
        notes: item.notes || null,
        selected_options: item.selected_options.map(opt => ({
          option_group: opt.option_group || '',
          option_name: opt.option_name,
          price_delta: opt.price_delta
        }))
      }));
      // Dine-in QR self-service: no contact info, no payment, no
      // delivery fee. The kitchen ticket is identified by the table name.
      const payload = {
        order_intent_id: orderIntentId,
        force_new_order: false,
        restaurant_id: Number(cart.restaurantId),
        customer_name: tableOrder.tableName || `Mesa ${tableOrder.tableNumber}`,
        customer_phone: null,
        customer_email: null,
        delivery_type: 'dine_in',
        payment_method: 'cash',
        delivery_address: null,
        delivery_lat: null,
        delivery_lng: null,
        maps_url: null,
        table_id: tableOrder.tableId,
        notes: null,
        subtotal,
        delivery_fee: 0,
        total: subtotal,
        source: 'qr',
        wants_invoice: false,
        items: itemsPayload
      };
      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok) {
        // The recent-order 409 confirmation flow doesn't apply to dine-in
        // QR self-service (no phone, so the recent-order guard is skipped
        // server-side anyway). Anything else here is a real failure.
        const errMsg = result?.error || result?.message || `Error del servidor (${response.status})`;
        throw new Error(errMsg);
      }
      const order = result?.order || result;
      const orderNumber = order?.order_number || '';
      const snapshotItems = [...cart.items];
      const snapshotTableName = tableOrder.tableName || `Mesa ${tableOrder.tableNumber}`;
      const snapshotTotal = subtotal;
      setRanchoOrderNumber(String(orderNumber || ''));
      setRanchoSuccessSnapshot({
        tableName: snapshotTableName,
        items: snapshotItems,
        total: snapshotTotal
      });
      setRanchoSubmitState('success');
      // Clear cart + table context AFTER snapshot so the success screen
      // still shows the order summary.
      clearCart();
      clearTableOrder();
    } catch (err: any) {
      console.error('[CartPage][RanchoQR] submit error:', err);
      setRanchoSubmitError(err?.message || 'No pudimos enviar tu pedido. Inténtalo de nuevo o llama al mesero.');
      setRanchoSubmitState('error');
    }
  };
  const handleCheckoutClick = () => {
    if (isClosedBySchedule) {
      alert(`${cart.restaurantName || 'El restaurante'} está cerrado en este momento. ${scheduleStatus.nextOpeningHint || 'Vuelve durante nuestro horario de atención.'}`);
      return;
    }
    // QR self-service dine-in: one-tap submit straight to the kitchen.
    // Cubre Rancho (28) y NatuCafé (19) — ambos usan la misma lógica:
    // sin formulario de contacto, sin pago, el ticket va directo a la
    // estación de cocina vía el trigger de print jobs y la orden
    // aparece en el KDS con su mesa.
    if (isQrSelfServiceTable) {
      submitRanchoQrOrder();
      return;
    }
    if (cart.items.length > 0 && !upsellAlreadyShown) {
      if (isCrackPolloCart) {
        setShowUpsellModal(true);
        return;
      }
      if (isEmiliusCart) {
        setShowEmiliusUpsellModal(true);
        return;
      }
      if (isKyomuCart) {
        setShowKyomuUpsellModal(true);
        return;
      }
    }
    navigate('/checkout');
  };
  const handleUpsellContinue = () => {
    setShowUpsellModal(false);
    setShowEmiliusUpsellModal(false);
    setShowKyomuUpsellModal(false);
    setUpsellAlreadyShown(true);
    navigate('/checkout');
  };
  // Register restaurant with central kitchen pause monitor
  useEffect(() => {
    if (cart.restaurantId) {
      console.log('[NOIZA][CartPage] Registering restaurant:', cart.restaurantId);
      monitorRestaurant(cart.restaurantId);
    }
  }, [cart.restaurantId, monitorRestaurant]);
  // La Cabaña Parrillera (id=24) charges the delivery fee manually from
  // the KDS once the order is taken — it's never quoted in the public
  // cart, so showing a "Costo de envío" line here just confuses the
  // customer (especially because they haven't even chosen delivery yet).
  const isCabanaParrilleraCart = String(cart.restaurantId) === '24';
  // Emilius Pizza (id=27) — el costo del express NO debe mostrarse ni
  // sumarse en el carrito: el cliente todavía no eligió envío vs. retiro.
  // El costo de envío se define en el checkout según el método elegido.
  // Mismo criterio que Cabaña: no cotizar envío en el carrito público.
  // In a real app, delivery fee would be calculated based on distance/settings
  // For Rancho QR (dine-in self-service), never show a delivery fee.
  const deliveryFeeEstimate = isCabanaParrilleraCart || isEmiliusCart || isQrSelfServiceTable || cart.items.length === 0 ? 0 : 1500;
  const total = subtotal + deliveryFeeEstimate;
  // ── Success state (Rancho QR) ────────────────────────────────────────
  if (ranchoSubmitState === 'success' && ranchoSuccessSnapshot) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-7029" data-mp="u12nsmzw37y2">
        <MarketplaceHeader data-id="element-7030" data-mp="p6ycjrjlcdr6" />
        <main className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-12" data-id="element-7031" data-mp="p66m0v4up67r">
          <div className="bg-white rounded-3xl border border-emerald-100 shadow-sm overflow-hidden" data-id="element-7032" data-mp="k8n2pgtaq38y">
            <div className="bg-emerald-50 px-6 py-8 text-center border-b border-emerald-100" data-id="element-7033" data-mp="1hpvbjw6cy9hl">
              <div className="w-16 h-16 rounded-full bg-emerald-500 mx-auto mb-4 flex items-center justify-center" data-id="element-7034" data-mp="1cwaov0a7t1xp">
                <CheckCircle2 className="w-9 h-9 text-white" data-id="element-7035" data-mp="1l8kqitlodio7" />
              </div>
              <h1 className="text-2xl font-bold text-emerald-900 mb-1" data-id="element-7036" data-mp="1p8lb6vzdaq3p">
                ¡Pedido enviado!
              </h1>
              <p className="text-emerald-700" data-id="element-7037" data-mp="1u9ivbe3xtu7u">
                Tu pedido se está preparando en cocina.
              </p>
            </div>
            <div className="px-6 py-6 space-y-4" data-id="element-7038" data-mp="2465fe0ofwh8t">
              <div className="flex items-center gap-3 bg-emerald-50/60 rounded-2xl px-4 py-3" data-id="element-7039" data-mp="2439eq4l7jego">
                <Utensils className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-7040" data-mp="284uo3fyj9am4" />
                <div data-id="element-7041" data-mp="2cyyvu1fmhmo9">
                  <p className="font-bold text-gray-900" data-id="element-7042" data-mp="2czqtdhss5uh6">
                    {ranchoSuccessSnapshot.tableName}
                  </p>
                  {ranchoOrderNumber && <p className="text-sm text-gray-600" data-id="element-7043" data-mp="2mx502powj4wa">
                      Pedido #{ranchoOrderNumber}
                    </p>}
                </div>
              </div>
              <div className="border border-gray-100 rounded-2xl divide-y divide-gray-100 overflow-hidden" data-id="element-7044" data-mp="227kjloaa425d">
                {ranchoSuccessSnapshot.items.map(item => <div key={item.id} className="px-4 py-3 flex justify-between items-start gap-4" data-id="element-7045" data-mp="2bx44ezhjy62v">
                    <div data-id="element-7046" data-mp="273sugphg196q">
                      <p className="font-medium text-gray-900" data-id="element-7047" data-mp="2kx0k4blyruls">
                        {item.quantity} × {item.item_name_snapshot}
                      </p>
                      {item.selected_options.length > 0 && <ul className="mt-1 text-xs text-gray-500 space-y-0.5" data-id="element-7048" data-mp="2uvq6agkw817u">
                          {item.selected_options.map((opt, idx) => <li key={idx} data-id="element-7049" data-mp="2puagycbon8jn" data-mpi={idx}>· {opt.option_name}</li>)}
                        </ul>}
                    </div>
                    <span className="text-sm font-semibold text-gray-700 whitespace-nowrap" data-id="element-7050" data-mp="vjnln30j90sl">
                      {formatCurrency((item.unit_price_snapshot + item.selected_options.reduce((sum, opt) => sum + (opt.price_delta || 0), 0)) * item.quantity)}
                    </span>
                  </div>)}
              </div>
              <div className="flex justify-between text-lg font-bold text-gray-900 pt-2" data-id="element-7051" data-mp="2ohau0zricoqs">
                <span data-id="element-7052" data-mp="2si4e1i1txjee">Total</span>
                <span data-id="element-7053" data-mp="2eha4ux6yjeeo">{formatCurrency(ranchoSuccessSnapshot.total)}</span>
              </div>
              <p className="text-sm text-gray-500 text-center pt-2" data-id="element-7054" data-mp="2jg3n12x6vtl6">
                Si necesitás algo, llamá al mesero. ¡Buen provecho!
              </p>
              <Button fullWidth onClick={() => navigate('/')} className="h-12" data-id="element-7055" data-mp="cq6veb8t05vp">
                Volver al inicio
              </Button>
            </div>
          </div>
        </main>
        <Footer data-id="element-7056" data-mp="2dqtwxklo37fb" />
      </div>;
  }
  if (cart.items.length === 0) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-7057" data-mp="28sjzf3cgzuqj">
        <MarketplaceHeader data-id="element-7058" data-mp="24qyfiy1iqg6u" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-7059" data-mp="24q5hclgmkz0b">
          <EmptyState icon={ShoppingBag} title={t('cart_empty')} description={t('cart_empty_desc')} actionLabel={isEmiliusCart ? 'Volver al menú' : t('explore')} onAction={() => navigate(isEmiliusCart ? `/r/${urlSlug || cart.restaurantSlug || 'emilius-pizza'}` : '/restaurants')} data-id="element-7060" data-mp="1zsdonp85p8v2" />
        </main>
        <Footer data-id="element-7061" data-mp="1h3gsfft786a7" />
      </div>;
  }
  return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-7062" data-mp="1c32kdxrlkwc6">
      <MarketplaceHeader data-id="element-7063" data-mp="173g7xqjlfk4f" />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8" data-id="element-7064" data-mp="1736fzxezn2w3">
        <div className="flex items-center justify-between mb-8" data-id="element-7065" data-mp="3iqs1wpsj5c5b">
          <div className="flex items-center gap-4" data-id="element-7066" data-mp="3iqjtba3fwszx">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 rounded-full hover:bg-gray-50 transition-colors" data-id="element-7067" data-mp="3np3eh8a7m0r8">
              <ArrowLeft className="w-6 h-6" data-id="element-7068" data-mp="lougyf3co1q" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-7069" data-mp="iua16w2v38b">
              {t('cart_title')}
            </h1>
          </div>
          <button onClick={clearCart} className="text-sm font-medium text-red-500 hover:text-red-600 transition-colors" data-id="element-7070" data-mp="34yvf919mrugb">
            {t('cart_clear')}
          </button>
        </div>

        {/* QR self-service table banner — Rancho (28) y NatuCafé (19) */}
        {isQrSelfServiceTable && tableOrder && <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 mb-6 flex items-center gap-3" data-id="element-7071" data-mp="2h9ass8e9qf2a">
            <Utensils className="w-5 h-5 text-emerald-600 shrink-0" data-id="element-7072" data-mp="2l9bbw6cbbefo" />
            <div data-id="element-7073" data-mp="3okqqtn74awtd">
              <p className="font-bold text-emerald-900" data-id="element-7074" data-mp="3ok8lm12h989b">
                {tableOrder.tableName || `Mesa ${tableOrder.tableNumber}`} ·
                Autoatención
              </p>
              <p className="text-sm text-emerald-700" data-id="element-7075" data-mp="267dlttvpnoh">
                Tu pedido se enviará directamente a la cocina.
              </p>
            </div>
          </div>}

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-8" data-id="element-7076" data-mp="35oiho8azi1ss">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center gap-3" data-id="element-7077" data-mp="30pe8hho2842g">
            <Store className="w-5 h-5 text-gray-500" data-id="element-7078" data-mp="3ep6vb1wgu055" />
            <span className="font-semibold text-gray-900" data-id="element-7079" data-mp="3amk0cs4hzz3q">
              {cart.restaurantName}
            </span>
          </div>

          <div className="px-6 py-2" data-id="element-7080" data-mp="tx54cscoqy7b">
            {cart.items.map(item => <CartItem key={item.id} item={item} onUpdateQuantity={updateQuantity} onRemove={removeItem} data-id="element-7081" data-mp="pwl6fmkafqk4" />)}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" data-id="element-7082" data-mp="12fym6czzn3lo">
          <div className="space-y-4" data-id="element-7083" data-mp="nig2hp263gl3">
            <div className="flex justify-between text-gray-600" data-id="element-7084" data-mp="njhb7mvpnqzn">
              <span data-id="element-7085" data-mp="shsm80xqs7jg">{t('subtotal')}</span>
              <span data-id="element-7086" data-mp="siu00d65784v">{formatCurrency(subtotal + (discount?.amount ?? 0))}</span>
            </div>
            {discount && <div className="flex justify-between text-emerald-600 font-medium" data-id="element-7087" data-mp="1l559yefn2lec">
                <span data-id="element-7088" data-mp="1l799ylaf309c">{discount.label}</span>
                <span data-id="element-7089" data-mp="16fh7vazmvipk">-{formatCurrency(discount.amount)}</span>
              </div>}
            {deliveryFeeEstimate > 0 && <div className="flex justify-between text-gray-600" data-id="element-7090" data-mp="1bdiq5tp8jdzz">
                <span data-id="element-7091" data-mp="1z44vvebrom6t">{t('delivery_fee')}</span>
                <span data-id="element-7092" data-mp="1z3d4ygt47cmv">{formatCurrency(deliveryFeeEstimate)}</span>
              </div>}
            <div className="pt-4 border-t border-gray-100 flex justify-between text-lg font-bold text-gray-900" data-id="element-7093" data-mp="23xr48uawe3wt">
              <span data-id="element-7094" data-mp="3v8kt0zbvukal">{t('total')}</span>
              <span data-id="element-7095" data-mp="3v7j4pt5uw1ax">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        {/* Error banner for QR self-service direct-submit (Rancho + NatuCafé) */}
        {isQrSelfServiceTable && ranchoSubmitState === 'error' && ranchoSubmitError && <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-4 mb-4 flex items-start gap-3" data-id="element-7096" data-mp="mrhn0jorl8yi">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-7097" data-mp="is8pd3qlsqxo" />
              <div className="flex-1" data-id="element-7098" data-mp="dr1hl4ognrr0">
                <p className="font-semibold text-red-900" data-id="element-7099" data-mp="dpzzwhcxw70b">
                  No pudimos enviar tu pedido
                </p>
                <p className="text-sm text-red-700 mt-0.5" data-id="element-7100" data-mp="2yi22xp9zuzei">
                  {ranchoSubmitError}
                </p>
              </div>
            </div>}

        <Button onClick={handleCheckoutClick} className="w-full h-14 text-lg" disabled={isKitchenPaused || isClosedBySchedule || ranchoSubmitState === 'submitting'} data-id="element-7101" data-mp="2thnrryd3j9c9">
          {ranchoSubmitState === 'submitting' ? <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-7102" data-mp="2tbdc9i5ryqb7" />
              Enviando a cocina...
            </> : isClosedBySchedule ? `🔒 Cerrado${scheduleStatus.nextOpeningHint ? ` — ${scheduleStatus.nextOpeningHint}` : ''}` : isKitchenPaused ? t('kitchen_paused') : isQrSelfServiceTable ? 'Enviar Pedido' : 'Continuar y elegir entrega'}
        </Button>
      </main>

      {isCrackPolloCart && cart.restaurantId && cart.restaurantName && <CheckoutUpsellModal isOpen={showUpsellModal} onClose={() => setShowUpsellModal(false)} onContinue={handleUpsellContinue} restaurantId={cart.restaurantId} restaurantName={cart.restaurantName} restaurantSlug={cart.restaurantSlug ?? undefined} data-id="element-7103" data-mp="1hqzuufzrxyro" />}

      {isEmiliusCart && cart.restaurantId && cart.restaurantName && <EmiliusCheckoutUpsellModal isOpen={showEmiliusUpsellModal} onClose={() => setShowEmiliusUpsellModal(false)} onContinue={handleUpsellContinue} restaurantId={cart.restaurantId} restaurantName={cart.restaurantName} restaurantSlug={cart.restaurantSlug ?? undefined} data-id="element-7104" data-mp="3nbuvil8jp0v" />}

      {isKyomuCart && cart.restaurantId && cart.restaurantName && <CheckoutUpsellModal isOpen={showKyomuUpsellModal} onClose={() => setShowKyomuUpsellModal(false)} onContinue={handleUpsellContinue} restaurantId={cart.restaurantId} restaurantName={cart.restaurantName} restaurantSlug={cart.restaurantSlug ?? undefined} categorySlugs={['extras', 'adicionales', 'salsas-extra', 'bebidas']} title="¿Deseas agregar algo más?" subtitle="Acompaña tu sushi con extras o bebidas:" data-id="element-7105" data-mp="mfecox9m5bcp" />}

      <Footer data-id="element-7106" data-mp="2gt0brbi96enq" />
    </div>;
}